import { DateInput, DateTimePicker } from '@mantine/dates';
import { Select } from '@/components';
import type { FilterDef, FilterValue, FilterValues } from '../types';

interface Props {
  def: FilterDef;
  draft: FilterValues;
  onChange: (key: string, value: FilterValue) => void;
  /** Resolved at runtime for `runtimeOptions` filters. */
  runtimeOptions?: { value: string; label: string }[];
}

const toDate = (v: unknown): Date | null => (v ? new Date(v as string | Date) : null);

export function FilterField({ def, draft, onChange, runtimeOptions }: Props) {
  if (def.type === 'select') {
    const parent = def.dependsOn ? (draft[def.dependsOn.key] as string | null) : null;
    const blocked = Boolean(def.dependsOn) && !parent;

    const options: { value: string; label: string }[] = def.runtimeOptions
      ? runtimeOptions ?? []
      : def.dependsOn
        ? (parent ? def.dependsOn.options(parent) : []).map((v) => ({ value: v, label: v }))
        : (def.options ?? []).map((v) => ({ value: v, label: v }));

    return (
      <Select
        label={def.label}
        description={def.description}
        placeholder={blocked ? def.dependsOn!.placeholder : def.placeholder}
        data={options}
        value={(draft[def.key] as string | null) ?? null}
        onChange={(v) => onChange(def.key, v)}
        disabled={blocked}
        searchable
        clearable
      />
    );
  }

  if (def.type === 'date') {
    return (
      <DateInput
        label={def.label}
        placeholder={def.placeholder}
        valueFormat="DD MMM YYYY"
        value={(draft[def.key] as Date | null) ?? null}
        onChange={(v) => onChange(def.key, toDate(v))}
        clearable
      />
    );
  }

  return (
    <DateTimePicker
      label={def.label}
      placeholder={def.placeholder}
      valueFormat="DD MMM YYYY HH:mm"
      value={(draft[def.key] as Date | null) ?? null}
      onChange={(v) => onChange(def.key, toDate(v))}
      clearable
    />
  );
}